// src/components/billing/Result.jsx
import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import theme from '../../theme';
import { useAuth } from '../../contexts/AuthContext';
import { isProEntitled } from '../../lib/entitlement';
import { useT } from '../../i18n/LanguageContext';

// After Stripe redirects back, the entitlement is written asynchronously by the
// webhook. Poll the user doc until Pro is active, then send them to the canvas.
export default function Result({ ok }) {
  const navigate = useNavigate();
  const t = useT();
  const { refreshUserData } = useAuth();
  const [status, setStatus] = useState(ok ? 'checking' : 'cancel'); // checking | active | timeout | cancel

  useEffect(() => {
    if (!ok) return undefined;
    let active = true;
    let tries = 0;
    const poll = async () => {
      let data = null;
      try { data = await refreshUserData(); } catch (e) { /* transient */ }
      if (!active) return;
      if (isProEntitled(data, new Date())) {
        setStatus('active');
        setTimeout(() => active && navigate('/dashboard'), 1100);
        return;
      }
      tries += 1;
      if (tries >= 12) { setStatus('timeout'); return; } // ~24s
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [ok, refreshUserData, navigate]);

  const ink = theme.home.ink;
  const body = {
    checking: t('billing.result.checkingBody'),
    active: t('billing.result.activeBody'),
    timeout: t('billing.result.timeoutBody'),
    cancel: t('billing.result.cancelBody'),
  }[status];
  const title = {
    checking: t('billing.result.checkingTitle'),
    active: t('billing.result.activeTitle'),
    timeout: t('billing.result.timeoutTitle'),
    cancel: t('billing.result.cancelTitle'),
  }[status];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', bgcolor: theme.home.paper, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{
          maxWidth: 480, width: '100%', textAlign: 'center', bgcolor: '#fff', borderRadius: 5,
          p: { xs: 4, md: 6 }, boxShadow: theme.home.softShadow, border: `1px solid ${theme.home.paperDeep}`,
        }}>
          <Typography variant="h3" sx={{ fontFamily: theme.home.display, fontWeight: 700, color: ink, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 2, color: theme.home.inkMuted, lineHeight: 1.6 }}>{body}</Typography>

          {status === 'checking' && <CircularProgress sx={{ mt: 4, color: theme.home.cyanText }} />}

          {(status === 'timeout' || status === 'cancel') && (
            <Button
              variant="contained" sx={{ mt: 4, px: 3.5, py: 1.3, borderRadius: 999, bgcolor: ink, '&:hover': { bgcolor: theme.home.inkHover } }}
              onClick={() => navigate(status === 'cancel' ? '/pricing' : '/dashboard')}
            >
              {status === 'cancel' ? t('billing.result.cancelButton') : t('billing.result.dashboardButton')}
            </Button>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
